<template>
    <v-container>
        <h2 class="mb-4">{{ mode === 'create' ? 'Create Article' : mode === 'edit' ? 'Edit Article' : 'View Article' }}</h2>
        <div v-if="mode && !['edit', 'create'].includes(mode)" class="edit-button-wrapper">
            <v-btn class="mx-2" dark medium color="primary" :loading="navigating" :disabled="navigating || submitting || deletingImage" @click="onEdit">
                <v-icon dark>mdi-account-edit</v-icon>
            </v-btn>
        </div>
        <v-form :readonly="readOnly">
            <v-menu
                :close-on-content-click="true"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-model="article.articleDate"
                        label="Choose article date"
                        prepend-icon="mdi-calendar"
                        v-bind="attrs"
                        :readonly="readOnly"
                        clearable
                        v-on="on"
                    />
                </template>
                <v-date-picker
                    v-model="article.articleDate"
                    :model-config="modelConfig"
                    class="datepicker"
                    mode="dateTime"
                />
            </v-menu>
            <v-text-field
                v-model="article.title"
                :rules="rules"
                label="Title"
                :clearable="clearable"
            />
            <v-select
                v-model="article.sport"
                :items="sports"
                item-text="title"
                item-value="id"
                return-object
                label="Sport"
                :clearable="clearable"
            />
            <div class="body-editor">
                <span class="body-label">Body</span>
                <editor
                    v-model="article.body"
                    :api-key="tinymceApiKey"
                    :init="editorInit"
                />
            </div>
            <v-file-input
                v-if="mode !== 'show'"
                v-model="newImage"
                label="Article Main Image"
                :rules="imageRules"
                filled
                show-size
                prepend-icon="mdi-camera"
                @change="createNewImagePreview"
            />
            <div v-if="previewImage && !previewImage.shouldDelete" class="image-input" :class="{'green-border': !previewImage.id}">
                <div v-if="mode === 'edit'" class="delete-btn">
                    <v-btn class="mx-2 white--text" small color="secondary" :loading="deletingImage" :disabled="deletingImage || submitting || navigating" @click="onImageDelete">
                        X
                    </v-btn>
                </div>
                <v-img v-if="previewImage.url" class="img-preview" :src="previewImage.url" contain height="180" />
                <v-text-field
                    v-model="previewImage.description"
                    label="Image Description"
                    :clearable="clearable"
                />
            </div>
                <v-file-input
                    v-if="mode !== 'show'"
                    v-model="newGalleryImages"
                    label="Article Gallery Images"
                    multiple
                    chips
                    small-chips
                    filled
                    show-size
                    prepend-icon="mdi-image-multiple"
                    @change="createGalleryPreviews"
                />
                <div v-if="visibleGalleryItems.length" class="gallery-wrapper">
                    <div v-for="item in visibleGalleryItems" :key="item.uid" class="gallery-item" :class="{ 'gallery-item-new': !item.id }">
                        <div v-if="mode !== 'show'" class="gallery-delete-btn">
                            <v-btn class="mx-1 white--text" x-small color="secondary" :loading="deletingImage" :disabled="deletingImage || submitting || navigating" @click="onGalleryImageDelete(item)">
                                X
                            </v-btn>
                        </div>
                        <v-img v-if="item.url" class="gallery-img-preview" :src="item.url" contain height="120" />
                    </div>
                </div>
        </v-form>
        <div v-if="mode !== 'show'" class="submit-button-wrapper">
            <v-btn class="mx-2 white--text" :loading="submitting" :disabled="shouldDisableSubmit() || submitting || navigating || deletingImage" large color="primary" @click="onSubmit">
                Submit
            </v-btn>
        </div>
    </v-container>
</template>

<script>
    import imageService from '@/services/image-service'
    import articleService from '@/services/article-service'
    import sportService from '@/services/sport-service'
    import Editor from '@tinymce/tinymce-vue'
    import moment from 'moment'

    export default {
        name: 'ArticleForm',
        components: { Editor },
        data: () => {
            return {
                mode: null,
                readOnly: false,
                clearable: true,
                available: null,
                apiUrl: process.env.VUE_APP_API_HOST_DOMAIN,
                tinymceApiKey: process.env.VUE_APP_TINYMCE_API_KEY,
                article: {
                    title: '',
                    body: '',
                    articleDate: null,
                    sport: null
                },
                sports: [],
                editorInit: {
                    selector: 'article_content',
                    images_upload_handler: function(file, success) {
                        let formData = new FormData()
                        formData.append('file', file.blob(), file.filename())
                        imageService.uploadNewImage(formData).then((response) => { success(process.env.VUE_APP_API_HOST_DOMAIN + '/uploads/' + response.data.image_url) })
                    },
                    menubar: false,
                    plugins: [
                        'advlist autolink lists charmap print preview anchor',
                        'searchreplace visualblocks fullscreen',
                        'insertdatetime table paste help wordcount link',
                        'image media'
                    ],
                    automatic_uploads: true,
                    powerpaste_allow_local_images: true,
                    file_picker_types: 'image',
                    resize: 'both',
                    min_height: 600,
                    fontsize_formats: '8px 10px 12px 14px 18px 24px 36px',
                    toolbar: 'undo redo | styleselect | forecolor | bold italic | fontselect fontsizeselect | emoticons | alignleft aligncenter alignright alignjustify | numlist bullist | outdent indent | link unlink | image media '
                },
                articleId: null,
                rules: [
                    v => !!v || 'Field is required'
                ],
                newImage: null,
                previewImage: null,
                newGalleryImages: [],
                existingGalleryImages: [],
                galleryPreviewImages: [],
                imageRules: [
                    value => !value || value.size < 2000000 || 'Image size should be less than 2 MB!'
                ],
                modelConfig: {
                    type: 'String',
                    mask: 'yyyy-MM-DD'
                },
                submitting: false,
                navigating: false,
                deletingImage: false
            }
        },
        created() {
            this.mode = this.$route.params.mode
            this.loadSports()
            if (this.mode === 'create') {
                return
            } else if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
                this.editorInit.setup = function(editor) {
                    editor.setMode('readonly')
                }
            }
            this.articleId = this.$route.params.articleId
            if (this.articleId) {
                articleService.getArticleById(this.articleId).then(resp => {
                    this.article = resp.data.article
                    this.syncArticleSport()
                    this.previewImage = this.article.image
                    if (this.previewImage) {
                        this.previewImage.url = process.env.VUE_APP_GOOGLE_BUCKET_URL + this.previewImage.imageName
                    }
                    this.existingGalleryImages = (this.article.imageGallery || []).map(image => ({
                        ...image,
                        url: process.env.VUE_APP_GOOGLE_BUCKET_URL + image.imageName,
                        shouldDelete: false,
                        uid: `existing-${image.id}`
                    }))
                    this.article.articleDate = this.formatDate(resp.data.article.articleDate, 'yyyy-MM-DD')
                })
            }
        },
        computed: {
            visibleGalleryItems() {
                return [...this.existingGalleryImages, ...this.galleryPreviewImages].filter(item => !item.shouldDelete)
            }
        },
        methods: {
            loadSports() {
                sportService.getAllSports().then(resp => {
                    this.sports = resp.data.data || []
                    this.syncArticleSport()
                })
            },
            syncArticleSport() {
                if (!this.article || !this.article.sport) {
                    return
                }
                const sportId = typeof this.article.sport === 'object' ? this.article.sport.id : this.article.sport
                if (!sportId) {
                    this.article.sport = null
                    return
                }
                const selectedSport = this.sports.find(sport => sport.id === sportId)
                this.article.sport = selectedSport || { id: sportId }
            },
            formatDate(date, format) {
                if (!date) {
                    return null
                }
                return moment(date).format(format)
            },
            shouldDisableSubmit() {
                return !this.article.title
            },
            async onEdit() {
                this.navigating = true
                try {
                    await this.$router.push(`/articles/${this.articleId}/edit`)
                } finally {
                    this.navigating = false
                }
            },
            async createArticle() {
                const response = await articleService.createArticle(this.buildArticleFormData())
                if (response.data.id) {
                    this.articleId = response.data.id
                }
            },
            async editArticle() {
                await articleService.editArticleById(this.articleId, this.buildArticleFormData())
            },
            async onSubmit() {
                this.submitting = true
                this.deleteUnecessaryFields()
                try {
                    if (this.mode === 'edit') {
                        await this.editArticle()
                    } else if (this.mode === 'create') {
                        await this.createArticle()
                    }
                    await this.$router.push(`/articles`)
                } finally {
                    this.submitting = false
                }
            },
            createNewImagePreview() {
                if (!this.hasSelectedImage(this.newImage)) {
                    this.previewImage = null
                    return
                }
                this.previewImage = this.newImage
                this.previewImage.url = URL.createObjectURL(this.newImage)
                this.previewImage.description = null
            },
            deleteUnecessaryFields() {
                delete this.article.id
                delete this.article.image
                delete this.article.imageGallery
                delete this.article.createdAt
            },
            buildArticleFormData() {
                const formData = new FormData()
                formData.append('title', this.article.title || '')
                formData.append('body', this.article.body || '')

                if (this.article.titleEn) {
                    formData.append('titleEn', this.article.titleEn)
                }
                if (this.article.bodyEn) {
                    formData.append('bodyEn', this.article.bodyEn)
                }
                if (this.article.articleDate) {
                    formData.append('articleDate', this.article.articleDate)
                }
                formData.append('sportId', this.article.sport ? this.article.sport.id : '')

                if (this.hasSelectedImage(this.newImage)) {
                    formData.append('file', this.newImage)
                }

                if (this.previewImage && this.previewImage.description) {
                    formData.append('mainImageDescription', this.previewImage.description)
                }

                if (this.previewImage && this.previewImage.id && this.previewImage.shouldDelete && !this.hasSelectedImage(this.newImage)) {
                    formData.append('removeMainImage', 'true')
                }

                this.galleryPreviewImages.forEach(image => {
                    if (image.file && !image.shouldDelete) {
                        formData.append('galleryFiles[]', image.file)
                    }
                })

                const galleryDeleteIds = this.existingGalleryImages
                    .filter(image => image.id && image.shouldDelete)
                    .map(image => image.id)

                if (galleryDeleteIds.length) {
                    formData.append('galleryDeleteIds', JSON.stringify(galleryDeleteIds))
                }

                return formData
            },
            createGalleryPreviews() {
                const files = Array.isArray(this.newGalleryImages) ? this.newGalleryImages : []
                this.galleryPreviewImages = files.filter(file => this.hasSelectedImage(file)).map((file, index) => ({
                    uid: `new-${file.name}-${file.size}-${index}`,
                    url: URL.createObjectURL(file),
                    file,
                    shouldDelete: false
                }))
            },
            onGalleryImageDelete(item) {
                this.deletingImage = true
                if (item.id) {
                    item.shouldDelete = true
                    this.existingGalleryImages = [...this.existingGalleryImages]
                } else {
                    const remaining = this.galleryPreviewImages.filter(image => image.uid !== item.uid)
                    this.galleryPreviewImages = remaining
                    this.newGalleryImages = remaining.map(image => image.file)
                }
                this.deletingImage = false
            },
            onImageDelete() {
                this.deletingImage = true
                if (this.previewImage && this.previewImage.id) {
                    this.previewImage.shouldDelete = true
                    this.previewImage.url = null
                    this.previewImage = { ...this.previewImage }
                } else {
                    this.newImage = null
                    this.previewImage = null
                }
                this.deletingImage = false
            },
            hasSelectedImage(image) {
                return image && typeof image === 'object' && image.size !== undefined && image.name
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}

.body-label {
    display: inline-block;
    margin-bottom: 8px;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    width: 100%;
    max-width: 100%;
    height: 180px;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}

.gallery-wrapper {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    margin-top: 8px;
    padding: 10px;
    border: 1px solid rgba(33, 48, 80, 0.16);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.75);
    margin-top: 12px;
}

.gallery-item {
    position: relative;
    border: 2px solid #d3d3d3;
    border-radius: 10px;
    margin-bottom: 2px;
    padding: 8px;
}

.gallery-item-new {
    border-color: #4caf50;
}

.gallery-delete-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 1;
}

.gallery-img-preview {
    width: 100%;
    height: 120px;
}
</style>


